import React, { useRef, useState } from "react";
import { Upload, ImageIcon, X, Loader2 } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Click-to-upload image picker that uploads directly to Cloudinary
 * using a signed signature from the backend.
 *
 * Props:
 *  - value: current secure_url (string)
 *  - onChange: (url) => void
 *  - folder: e.g. "zaykaqr/restaurants/<id>/logo" or "zaykaqr/blogs/featured"
 *  - label: optional label text shown above the dropzone
 *  - aspect: optional Tailwind class for box ratio (default "aspect-video")
 *  - testId
 */
export const ImageUpload = ({
  value,
  onChange,
  folder,
  label,
  aspect = "aspect-video",
  testId = "image-upload",
}) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const pick = () => inputRef.current?.click();

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8 MB");
      return;
    }
    setBusy(true);
    setProgress(0);
    const oldUrl = value;
    try {
      // 1) Ask backend for a signature
      const { data: sig } = await api.get("/cloudinary/signature", { params: { folder } });

      // 2) Upload directly to Cloudinary
      const fd = new FormData();
      fd.append("file", file);
      fd.append("api_key", sig.api_key);
      fd.append("timestamp", sig.timestamp);
      fd.append("signature", sig.signature);
      fd.append("folder", sig.folder);

      const url = `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`;
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", url);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)); }
            catch { reject(new Error("Invalid Cloudinary response")); }
          } else reject(new Error(xhr.responseText || `HTTP ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error("Upload failed"));
        xhr.send(fd);
      });

      if (!result.secure_url) throw new Error("No URL returned");
      onChange(result.secure_url);
      // Best-effort: delete the previous asset so it doesn't pile up
      if (oldUrl && oldUrl.includes("res.cloudinary.com")) {
        api.delete("/cloudinary/asset", { params: { url: oldUrl } }).catch(() => {});
      }
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clear = async () => {
    const oldUrl = value;
    onChange("");
    if (oldUrl && oldUrl.includes("res.cloudinary.com")) {
      api.delete("/cloudinary/asset", { params: { url: oldUrl } }).catch(() => {});
    }
  };

  return (
    <div data-testid={testId}>
      {label && <label className="text-xs font-medium text-zq-muted block mb-1.5">{label}</label>}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testId}-input`}
      />
      {value ? (
        <div className={`relative rounded-lg overflow-hidden border border-zq-border ${aspect}`}>
          <img src={value} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition flex items-center justify-center gap-2 opacity-0 hover:opacity-100">
            <button type="button" className="zq-btn zq-btn-primary !py-1.5 text-xs" onClick={pick} data-testid={`${testId}-replace`}>
              <Upload size={14}/> Replace
            </button>
            <button type="button" className="zq-btn zq-btn-outline !py-1.5 text-xs" onClick={clear} data-testid={`${testId}-clear`}>
              <X size={14}/> Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          className={`w-full ${aspect} rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-2 transition cursor-pointer
            ${busy ? "bg-zq-orange-light border-zq-orange" : "bg-white border-zq-border hover:border-zq-orange hover:bg-zq-orange-light"}`}
          data-testid={`${testId}-button`}
        >
          {busy ? (
            <>
              <Loader2 className="text-zq-orange animate-spin" size={28}/>
              <div className="text-xs text-zq-muted">Uploading… {progress}%</div>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-full bg-zq-orange-light flex items-center justify-center">
                <Upload className="text-zq-orange" size={22}/>
              </div>
              <div className="text-sm font-medium text-zq-charcoal flex items-center gap-1">
                <ImageIcon size={14} className="text-zq-orange"/> Click to upload image
              </div>
              <div className="text-[10px] text-zq-muted">JPG, PNG, WebP · up to 8 MB</div>
            </>
          )}
        </button>
      )}
    </div>
  );
};
